"use client";

import type { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { withLoyaltyStore } from "@jti/loyalty/utils/withLoyaltyStore";

import { IMAGE_FILE_TYPES, VIDEO_FILE_TYPES } from "@/libs/builder/consts";
import { fullRichtextControls } from "@/types/richtext-controls";

export const loyaltyHeroDefaultValues = {
  backgroundType: "image",
  mobileVariant: "transparentContentOnTop",
  videoFileReference: "",
  tabletVideoFileReference: "",
  mobileVideoFileReference: "",
  displayScrollBar: false,
  logoReference: "",
  clipped: false,
  legalDisclaimer: "",
  desktopImage: {
    src: "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F8ffca99c7ff4421f904e63edc9935589",
    title: "desktopImage",
    alt: "desktopImage",
  },
  smallImage: {
    src: "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F8ffca99c7ff4421f904e63edc9935589",
    title: "smallImage",
    alt: "smallImage",
  },
  content: {
    title: "Title",
    restrictedTitle: "Restricted Title",
    tagLine: "XPRESS YOUR UNIQUENESS",
    text: "Description",
    restrictedText: "Restricted Text",
    cta: [
      {
        additionalBehavior: "always",
        label: "Button",
        theme: "light",
        layout: "full",
        tracking: "tracking",
        link: "/",
        openIn: "new Tab",
        target: "",
      },
    ],
  },
};

export const heroLoyaltyInputs = [
  {
    name: "data",
    type: "object",
    required: true,
    subFields: [
      {
        name: "backgroundType",
        type: "string",
        enum: ["image", "video"],
        required: true,
        defaultValue: "image",
      },
      {
        name: "theme",
        type: "string",
        enum: ["light", "dark", "inherit"],
        defaultValue: "dark",
      },
      {
        name: "mobileVariant",
        type: "string",
        defaultValue: "transparentContentOnTop",
        enum: ["transparentContentOnTop", "contentAtTheBottomWithBackground"],
        required: true,
      },
      {
        name: "logo",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "logoAlt",
        type: "string",
        defaultValue: "Logo",
      },
      {
        name: "logoWidth",
        type: "number",
      },
      {
        name: "logoHeight",
        type: "number",
      },
      {
        name: "content",
        type: "object",
        defaultValue: {
          title: "Title",
        },
        required: true,
        subFields: [
          {
            name: "tagLine",
            type: "string",
          },
          {
            name: "restrictedContent",
            type: "boolean",
            defaultValue: false,
          },
          {
            name: "title",
            type: "string",
          },
          {
            name: "restrictedTitle",
            friendlyName: "Title for restricted user",
            required: true,
            type: "string",
            showIf: `options.get("restrictedContent") === true`,
          },
          {
            name: "titleLogged",
            type: "string",
          },
          {
            name: "subheadline",
            type: "string",
          },
          {
            name: "subheadlineLogged",
            type: "string",
          },
          {
            name: "text",
            type: "richTextEditor",
            options: {
              controls: fullRichtextControls,
            },
          },
          {
            name: "textLogged",
            type: "richTextEditor",
            options: {
              controls: fullRichtextControls,
            },
          },
          {
            name: "restrictedText",
            friendlyName: "Description for restricted user",
            required: true,
            type: "string",
            showIf: `options.get("restrictedContent") === true`,
          },
          {
            name: "cta",
            type: "list",
            subFields: [
              {
                name: "additionalBehavior",
                type: "string",
                defaultValue: "always",
                required: true,
                enum: ["always", "onlyForLoggedIn", "onlyForNotLoggedIn", "onlyForRestrictedUsers"],
              },
              {
                name: "label",
                type: "string",
                required: true,
              },
              {
                name: "theme",
                type: "string",
                defaultValue: "light",
                enum: ["light", "dark"],
                required: true,
              },
              {
                name: "layout",
                type: "string",
                enum: ["chevron", "full", "border"],
                required: true,
              },
              {
                name: "tracking",
                type: "string",
                required: true,
              },
              {
                name: "link",
                type: "url",
                required: true,
              },
              {
                name: "openIn",
                type: "string",
                enum: ["new Tab", "same Tab"],
                friendlyName: "Open in",
                onChange: `options.set("target", (options.get("openIn") === "new Tab"? "_blank" : ""))`,
              },
              {
                hideFromUI: true,
                name: "target",
                type: "string",
                enum: ["", "_blank"],
                required: true,
                defaultValue: "",
              },
            ],
          },
        ],
      },
      {
        name: "desktopImage",
        type: "object",
        showIf: `options.get("backgroundType") === "image"`,
        subFields: [
          {
            name: "src",
            type: "file",
            required: true,
            allowedFileTypes: IMAGE_FILE_TYPES,
          },
          {
            name: "title",
            type: "string",
            required: true,
          },
          {
            name: "alt",
            type: "string",
            required: true,
          },
        ],
      },
      {
        name: "mediumImage",
        type: "object",
        showIf: `options.get("backgroundType") === "image"`,
        subFields: [
          {
            name: "src",
            type: "file",
            allowedFileTypes: IMAGE_FILE_TYPES,
          },
          {
            name: "title",
            type: "string",
          },
          {
            name: "alt",
            type: "string",
          },
        ],
      },
      {
        name: "smallImage",
        type: "object",
        showIf: `options.get("backgroundType") === "image"`,
        subFields: [
          {
            name: "src",
            type: "file",
            allowedFileTypes: IMAGE_FILE_TYPES,
            required: true,
          },
          {
            name: "title",
            type: "string",
            required: true,
          },
          {
            name: "alt",
            type: "string",
            required: true,
          },
        ],
      },
      {
        name: "videoFileReference",
        type: "file",
        allowedFileTypes: VIDEO_FILE_TYPES,
        showIf: `options.get("backgroundType") === "video"`,
      },
      {
        name: "tabletVideoFileReference",
        type: "file",
        allowedFileTypes: VIDEO_FILE_TYPES,
        showIf: `options.get("backgroundType") === "video"`,
      },
      {
        name: "mobileVideoFileReference",
        type: "file",
        allowedFileTypes: VIDEO_FILE_TYPES,
        showIf: `options.get("backgroundType") === "video"`,
      },
      {
        name: "displayScrollBar",
        type: "boolean",
        defaultValue: false,
        required: true,
      },
      {
        name: "logoReference",
        type: "string",
      },
      {
        name: "clipped",
        type: "boolean",
        defaultValue: false,
        required: true,
      },
      {
        name: "legalDisclaimer",
        type: "string",
      },
      {
        name: "gradientPosition",
        friendlyName: "gradient position desktop",
        type: "string",
        enum: ["none", "left", "top"],
        defaultValue: "top",
      },
      {
        name: "gradientPositionTablet",
        friendlyName: "gradient position tablet",
        type: "string",
        enum: ["none", "left", "top"],
        defaultValue: "top",
      },
      {
        name: "gradientPositionMobile",
        friendlyName: "gradient position mobile",
        type: "string",
        enum: ["none", "left", "top"],
        defaultValue: "top",
      },
    ],
    defaultValue: loyaltyHeroDefaultValues,
  },
] as const satisfies Inputs;

export const LoyaltyHeroComponent: RegisteredComponent = {
  name: "LoyaltyHero",
  friendlyName: "Loyalty Hero Banner",
  component: dynamic(async () => withLoyaltyStore((await import("./LoyaltyHero")).LoyaltyHero)),
  inputs: heroLoyaltyInputs,
  requiresParent: {
    query: {
      "component.name": { $in: ["LoyaltyWrapper"] },
    },
    message: "'LoyaltyHero' component can only be added inside a 'LoyaltyWrapper'",
  },
};
